import type { HistoryEntry, IndicatorDef } from "../engine/indicators";

interface Props {
  history: HistoryEntry[];
  def: IndicatorDef;
}

const LEVELS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

/**
 * Trajectoire complète d'un indicateur. Depuis le sprint lisibilité
 * (2026-06-10) l'historique couvre tout le mandat (61 tours) : on rend
 * une barre par tour en flux continu, avec les bornes (premier/dernier
 * tour, valeurs) et min/max en légende — l'ancienne version affichait
 * une colonne étiquetée par tour, illisible au-delà de ~12.
 */
export function HistoryGraph({ history, def }: Props) {
  if (history.length < 2) {
    return (
      <p className="graph-empty">
        Historique vide — au moins deux tours sont nécessaires pour voir la
        trajectoire.
      </p>
    );
  }

  const values = history.map((h) => h.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  const bars = values
    .map((v) => {
      if (range < 1e-9) return LEVELS[3];
      const level = Math.round(((v - min) / range) * (LEVELS.length - 1));
      return LEVELS[Math.max(0, Math.min(LEVELS.length - 1, level))];
    })
    .join("");

  const first = history[0];
  const last = history[history.length - 1];

  return (
    <div className="history-graph">
      <div className="graph-bars-flow" aria-hidden="true">
        {bars}
      </div>
      <div className="graph-axis">
        <span>
          T{first.turn} · {def.format(first.value)}
          {def.unit}
        </span>
        <span>
          T{last.turn} · {def.format(last.value)}
          {def.unit}
        </span>
      </div>
      {range >= 1e-9 && (
        <div className="graph-minmax">
          min {def.format(min)}
          {def.unit} · max {def.format(max)}
          {def.unit} · {history.length} tours
        </div>
      )}
    </div>
  );
}
